import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info, Volume2, VolumeX } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

interface InfoTooltipProps {
  title: string;
  content: string;
  className?: string;
}

interface TooltipPosition {
  top: number;
  left: number;
  placement: 'bottom' | 'top';
  arrowLeft: number;
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({ title, content, className }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition>({ top: 0, left: 0, placement: 'bottom', arrowLeft: 16 });
  const [isSpeaking, setIsSpeaking] = useState(false);
  const iconRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<number | undefined>(undefined);
  const hideTimeoutRef = useRef<number | undefined>(undefined);

  const TOOLTIP_WIDTH = 320;
  const TOOLTIP_OFFSET = 10;
  const VIEWPORT_PADDING = 12;

  const calculatePosition = () => {
    if (!iconRef.current) return;

    const iconRect = iconRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRef.current?.offsetHeight ?? 160;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Vertical: prefer below, flip above if not enough space
    const spaceBelow = viewportHeight - iconRect.bottom;
    const spaceAbove = iconRect.top;
    const placement: 'bottom' | 'top' =
      spaceBelow >= tooltipHeight + TOOLTIP_OFFSET || spaceBelow >= spaceAbove
        ? 'bottom'
        : 'top';

    const top =
      placement === 'bottom'
        ? iconRect.bottom + TOOLTIP_OFFSET + window.scrollY
        : iconRect.top - tooltipHeight - TOOLTIP_OFFSET + window.scrollY;

    // Horizontal: align right of icon, but clamp to viewport
    // Ideal: right edge of tooltip aligns with right edge of icon
    let left = iconRect.right - TOOLTIP_WIDTH + window.scrollX;

    // Clamp so tooltip doesn't go off the left edge
    if (left < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING;
    }

    // Clamp so tooltip doesn't go off the right edge
    if (left + TOOLTIP_WIDTH > viewportWidth - VIEWPORT_PADDING) {
      left = viewportWidth - VIEWPORT_PADDING - TOOLTIP_WIDTH;
    }

    // Arrow should point at the icon centre, relative to tooltip left
    const arrowLeft = Math.round(iconRect.left + iconRect.width / 2 - left - window.scrollX);

    setTooltipPos({ top, left, placement, arrowLeft });
  };

  useEffect(() => {
    if (isVisible) {
      // Run once immediately, then again after render so we have the real height
      calculatePosition();
      const raf = requestAnimationFrame(() => calculatePosition());
      return () => cancelAnimationFrame(raf);
    }
  }, [isVisible]);

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hoverTimeoutRef.current = window.setTimeout(() => setIsVisible(true), 300);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hideTimeoutRef.current = window.setTimeout(() => {
      setIsVisible(false);
      stopSpeech();
    }, 200);
  };

  const handleTooltipMouseEnter = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
  };

  const handleTooltipMouseLeave = () => {
    hideTimeoutRef.current = window.setTimeout(() => {
      setIsVisible(false);
      stopSpeech();
    }, 200);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible(!isVisible);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (
      tooltipRef.current &&
      !tooltipRef.current.contains(e.target as Node) &&
      iconRef.current &&
      !iconRef.current.contains(e.target as Node)
    ) {
      setIsVisible(false);
      stopSpeech();
    }
  };

  useEffect(() => {
    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', calculatePosition, true);
      window.addEventListener('resize', calculatePosition);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', calculatePosition, true);
        window.removeEventListener('resize', calculatePosition);
      };
    }
  }, [isVisible]);

  // Convert text to natural speech format
  const convertToNaturalSpeech = (text: string): string => {
    let naturalText = text;
    naturalText = naturalText.replace(/£50K/gi, 'fifty thousand pounds');
    naturalText = naturalText.replace(/£247K/gi, 'two hundred and forty-seven thousand pounds');
    naturalText = naturalText.replace(/£22K/gi, 'twenty-two thousand pounds');
    naturalText = naturalText.replace(/£63K/gi, 'sixty-three thousand pounds');
    naturalText = naturalText.replace(/£45K/gi, 'forty-five thousand pounds');
    naturalText = naturalText.replace(/£85K/gi, 'eighty-five thousand pounds');
    naturalText = naturalText.replace(/£125K/gi, 'one hundred and twenty-five thousand pounds');
    naturalText = naturalText.replace(/£150K/gi, 'one hundred and fifty thousand pounds');
    naturalText = naturalText.replace(/£285K/gi, 'two hundred and eighty-five thousand pounds');
    naturalText = naturalText.replace(/£380K/gi, 'three hundred and eighty thousand pounds');
    naturalText = naturalText.replace(/£38K/gi, 'thirty-eight thousand pounds');
    naturalText = naturalText.replace(/£15K/gi, 'fifteen thousand pounds');
    naturalText = naturalText.replace(/£100K/gi, 'one hundred thousand pounds');
    naturalText = naturalText.replace(/£(\d+)K/gi, (_, num) => `${parseInt(num)} thousand pounds`);
    return naturalText;
  };

  const speakText = () => {
    if (isSpeaking) {
      stopSpeech();
      return;
    }
    window.speechSynthesis.cancel();
    const fullText = `${title}. ${content}`;
    const naturalText = convertToNaturalSpeech(fullText);
    const utterance = new SpeechSynthesisUtterance(naturalText);
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const britishVoice = voices.find(
      (voice) =>
        (voice.lang === 'en-GB' && voice.name.includes('Google')) ||
        (voice.lang === 'en-GB' && voice.name.includes('Microsoft')) ||
        (voice.lang === 'en-GB' && voice.name.includes('Daniel')) ||
        (voice.lang === 'en-GB' && voice.name.includes('Kate')) ||
        voice.lang === 'en-GB'
    );
    if (britishVoice) utterance.voice = britishVoice;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeech = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  useEffect(() => {
    const loadVoices = () => window.speechSynthesis.getVoices();
    loadVoices();
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
      stopSpeech();
    };
  }, []);

  return (
    <div className={cn('relative inline-block', className)}>
      <button
        ref={iconRef}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors cursor-help"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        aria-label={`Information about ${title}`}
      >
        <Info className="h-4 w-4" />
      </button>

      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            className="fixed z-[99999] bg-card border shadow-lg rounded-lg p-4 animate-in fade-in-0 zoom-in-95"
            style={{
              width: `${TOOLTIP_WIDTH}px`,
              top: `${tooltipPos.top}px`,
              left: `${tooltipPos.left}px`,
            }}
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleTooltipMouseLeave}
          >
            {/* Arrow pointing at the icon */}
            <div
              className={cn(
                'absolute w-0 h-0 border-l-8 border-r-8 border-l-transparent border-r-transparent',
                tooltipPos.placement === 'bottom'
                  ? 'top-0 -translate-y-full border-b-8 border-b-card'
                  : 'bottom-0 translate-y-full border-t-8 border-t-card'
              )}
              style={{ left: `${tooltipPos.arrowLeft}px` }}
            />

            <div className="space-y-3">
              <div className="font-semibold text-sm">{title}</div>
              <div className="text-sm text-muted-foreground leading-relaxed">{content}</div>
              <div className="flex justify-end pt-2 border-t">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={speakText}
                  className="gap-2"
                >
                  {isSpeaking ? (
                    <>
                      <VolumeX className="h-4 w-4" />
                      Stop
                    </>
                  ) : (
                    <>
                      <Volume2 className="h-4 w-4" />
                      Listen
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
